/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Show claim code button
 */
import { Tooltip, Flex, Typography, Card } from 'antd';
import { TooltipPlacement } from 'antd/lib/tooltip';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { CODE_SET_MAPPING, CODE_STATUS_MAPPING } from 'app-constants/codeExplorer';
import { CodeTitle } from 'components/codeExplorer/CodeWidgets';
import { getCodeDetailedViewPath } from 'utils/router';
import { getTruncatedText } from 'utils/codeExplorer/icdParser';

export interface IProps {
	codeDetails: CodeExplorer.ICodeSummary;
}

const ShowClaimCode: React.FC<IProps> = ({ codeDetails }) => {
	const elementRef = useRef<HTMLSpanElement>(null);
	const [placement, setPlacement] = useState<TooltipPlacement>('top');

	useEffect(() => {
		if (elementRef.current) {
			const rect = elementRef.current.getBoundingClientRect();
			const tooltipWidth = 450;
			const viewportWidth = window.innerWidth;
			if (rect.left + tooltipWidth > viewportWidth) {
				setPlacement('left');
			}
		}
		// eslint-disable-next-line react-hooks/refs
	}, [elementRef.current]);

	return (
		<Tooltip
			arrow={false}
			classNames={{ root: 'code-claim-tooltip' }}
			placement={placement}
			title={
				<Card
					variant='borderless'
					title={
						<Typography.Title level={5} className='m-0'>
							Code Summary
						</Typography.Title>
					}
				>
					<Flex vertical gap={4} className='w-full'>
						<Flex align='center' justify='space-between'>
							<Flex align='center' gap={8}>
								<CodeTitle code={codeDetails?.code} codeSet={codeDetails?.codeset} modifier={codeDetails?.ismodifier} />
							</Flex>
						</Flex>
						<Link
							to={getCodeDetailedViewPath({ codeSet: codeDetails.codeset, code: codeDetails.displaycode })}
							target='_blank'
							rel='noopener noreferrer'
							className='self-start'
						>
							{getTruncatedText(codeDetails?.shortdescription, 150)}
						</Link>
						{codeDetails?.status === CODE_STATUS_MAPPING.terminated && codeDetails?.codeset === CODE_SET_MAPPING.CPT ? null : (
							<Typography.Text type='secondary'>{getTruncatedText(codeDetails?.longdescription, 300)}</Typography.Text>
						)}
					</Flex>
				</Card>
			}
		>
			<span ref={elementRef} className='text-primary-light cursor-pointer'>
				{codeDetails?.code}
			</span>
		</Tooltip>
	);
};

export default ShowClaimCode;
